import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { Firestore } from '@angular/fire/firestore';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  getMultiFactorResolver,
  MultiFactorError,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { environment } from '../../environments/environment';
import { InternetLostComponent } from '../material-dialog/internet-lost/internet-lost.component';
import { ModalPopupComponent } from '../modal-popup/modal-popup.component';
import { AuthService } from '../shared/auth.service';
import dayjs from '../shared/dayjs.config';
import { SettingsService } from '../shared/settings.service';
import { UserService } from '../shared/users.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  @HostBinding('class') hostClass = `${environment.name}`;
  // modalShow = false;
  isLoader = false;
  showLogin = false;
  loggedOut = false;
  authStateCalled = false;
  environmentName: string;
  private authUnsubscribe: (()=> void) | null = null;
  verificationCode = '';
  verificationCodeError = '';
  showMfaScreen = false;
  mfaError!: MultiFactorError;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly settingsService: SettingsService,
    private readonly userService: UserService,
    public readonly firestore: Firestore,
    private readonly auth: Auth,
    private readonly dialog: MatDialog
  ) {
    this.environmentName = environment.name;
    const user = localStorage.getItem('userSession');
    const url = localStorage.getItem('isBack');
    if (user == null) {
      this.checkAuthState();
    } else if (user != 'null' && url == 'yes') {
      this.checkAuthState();
    } else {
      localStorage.setItem('isBack', '');
      this.router.navigate(['/home/stream-view']);
    }
  }

  ngOnInit(): void {
    if (!this.authStateCalled) {this.checkAuthState();}
  }

  ngOnDestroy(): void {
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
    }
  }

  checkAuthState() {
    if (!window.navigator.onLine) {
      this.openInternetLost();
      return;
    }
    this.authStateCalled = true;
    this.loggedOut = this.authService.loggedOut;

    this.authUnsubscribe = onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        this.showLogin = false;
        await this.usersCollection(user.uid);
        await this.userService.updateUserActivity(user.uid, dayjs().toDate());
      } else if (this.authService.loggedOut) {
        this.showLogin = true;
        this.isLoader = false;
      } else {
        setTimeout(() => {
          this.showLogin = true;
          this.isLoader = false;
          this.authService.logout();
        }, 2000);
      }
    });
  }

  async login() {
    if (!window.navigator.onLine) {
      this.openInternetLost();
    } else {
      this.isLoader = true;
      // CustomToken is only for signing in via cypress automation testing
      const customToken = localStorage.getItem('customToken');
      try {
        if (customToken) {
          const cypressUser = await this.authService.cypressLogin(customToken);
          if (cypressUser.user) {
            await this.usersCollection(cypressUser.user.uid);
            await this.userService.updateUserActivity(cypressUser.user.uid, new Date());
          }
        } else {
          const result = await this.authService.loginWithPopup();
          if (result) {
            this.checkAuthState();
          }
        }
      } catch (error: any) {
        if (error.code === 'auth/multi-factor-auth-required') {
          this.showMfaScreen = true;
          this.mfaError = error;
          this.isLoader = false;
        }
      }
    }
  }
  
  async verifyCode() {
    this.isLoader = true;
    const mfaResolver = getMultiFactorResolver(this.auth, this.mfaError);
    const { factorId, uid } =
      mfaResolver.hints.find(
        ({ factorId }) => factorId === TotpMultiFactorGenerator.FACTOR_ID
      ) || {};

    if (!factorId || !uid) {
      throw this.mfaError;
    }

    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
      uid,
      this.verificationCode
    );
    try {
      await mfaResolver.resolveSignIn(multiFactorAssertion);
      this.checkAuthState();
    } catch (error: any) {
      this.verificationCodeError = error.code;
      this.isLoader = false;
      this.showLogin = true;
    }
  }

  openInternetLost() {
    this.dialog.open(InternetLostComponent, {
      width: '450px',
      panelClass: 'internet-lost',
      position: {
        top: '180px',
      },
      // disableClose: true,
    });
  }

  async usersCollection(id: string) {
    try {
      await this.settingsService.getSettingsData(id);
      localStorage.setItem('isBack', '');
      this.router.navigate(['home/stream-view']);
    } catch (err) {
      this.openError();
      this.isLoader = false;
      this.showLogin = true;
      this.authService.logout();
    }
  }

  openError() {
    this.dialog.open(ModalPopupComponent, {
      width: '468px',
      position: {
        top: '175px',
      },
      disableClose: true,
      panelClass: 'cancel-action',
    });
  }
}
